<template>
  <TColorPicker
    :default-color="currentColor"
    :colors="colors"
    :inverse="inverse"
    :light="!colorPickerDark"
    v-tooltip="{ content: 'Text color' }"
    @color-change="setColor"
  >
    <template #buttonIcon>
      <svg
        fill="#000000"
        width="20px"
        height="20px"
        viewBox="0 0 36 36"
        version="1.1"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <path
          d="M19.47,3.84a1.45,1.45,0,0,0-1.4-1H18a1.45,1.45,0,0,0-1.42,1L8.42,21.56a1.35,1.35,0,0,0-.14.59,1,1,0,0,0,1,1,1.11,1.11,0,0,0,1.08-.77l2.08-4.65h11l2.08,4.59a1.24,1.24,0,0,0,1.12.83,1.08,1.08,0,0,0,1.08-1.08,1.59,1.59,0,0,0-.14-.57ZM13.36,15.71,18,5.49l4.6,10.22Z"
          fill="currentColor"
        ></path>
        <rect
          x="4.06"
          y="25"
          width="28"
          height="8"
          rx="2"
          ry="2"
          :fill="currentColor || '#000000'"
        ></rect>
        <rect x="0" y="0" width="36" height="36" fill-opacity="0" />
      </svg>
      <!-- <svg
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 52 52"
        enable-background="new 0 0 52 52"
        xml:space="preserve"
      >
        <path
          d="M10.4,36h4.1c0.6,0,1.2-0.5,1.4-1.1l3.2-8.9h13.4l3.5,8.9c0.2,0.6,0.8,1.1,1.4,1.1h4.1
	c0.7,0,1.2-0.7,0.9-1.3L30.4,5c-0.2-0.6-0.7-1-1.3-1H22c-0.6,0-1.2,0.4-1.4,1l-11,29.7C9.3,35.3,9.8,36,10.4,36z M25.1,10H26l4.3,10
	h-9L25.1,10z"
          fill="currentColor"
        />
        <path
          d="M48.5,42h-45C2.7,42,2,42.7,2,43.5v3C2,47.3,2.7,48,3.5,48h45c0.8,0,1.5-0.7,1.5-1.5v-3
	C50,42.7,49.3,42,48.5,42z"
          :fill="currentColor || '#000000'"
        />
      </svg> -->
    </template>
  </TColorPicker>
</template>

<script lang="ts" setup>
import {
  expandSelectionToWord,
  getActiveMarks,
} from "../_internal/cutomExtensions";
import { TColorPicker } from "@topol/twig-ui";
import { Editor } from "@tiptap/core";
import { computed, ref, watch } from "vue";

const props = defineProps<{
  editor: Editor;
  colors?: string[];
  colorPickerDark?: boolean;
  inverse?: boolean;
}>();

const localColor = ref<string | null>(null);
const previousColor = ref<string>("");

const editorColor = computed(() => {
  setPreviousColor();
  if (!props.editor) return "";

  // Get marks including stored marks (for empty paragraphs after Enter)
  const marks = getActiveMarks(props.editor);
  const textStyle = marks.find((mark) => mark.type.name === "textStyle");
  // A `var(...)` reference (a consumer can remap a picked color to one, to
  // keep it linked to a live theme value) isn't a color the picker's own
  // RGB/HSV math can parse for its swatch preview -- fall through to the DOM
  // fallback below instead, which reads the browser's already-resolved value.
  if (textStyle?.attrs?.color && !textStyle.attrs.color.startsWith("var(")) {
    return textStyle.attrs.color;
  }

  const { state } = props.editor;
  const { $from } = state.selection;

  const nodeFont = $from.parent.attrs?.color;
  if (nodeFont && !nodeFont.startsWith("var(")) {
    return nodeFont;
  }

  // 3️⃣ Fallback: zjisti z DOM
  if (typeof window !== "undefined" && window.getSelection) {
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      let node = selection.anchorNode as HTMLElement | null;

      if (node?.classList?.contains("tiptap") && node.firstElementChild) {
        node = node.firstElementChild as HTMLElement;
      }

      // Najdi rodiče, co je prvek
      while (node && node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
      }

      let parentEditor = node;
      let isInEditor = false;
      while (parentEditor) {
        if (parentEditor.classList.contains("tiptap-editor")) {
          isInEditor = true;
          break;
        }
        parentEditor = parentEditor.parentElement as HTMLElement | null;
      }

      if (node && isInEditor) {
        const style = window.getComputedStyle(node);
        return style.color || "";
      }
    }
  }
  return previousColor.value;
});

const currentColor = computed(() => localColor.value ?? editorColor.value);

function setColor(color: string) {
  if (!props.editor) return;
  localColor.value = color;
  expandSelectionToWord(props.editor);
  // Apply to the editor's preserved selection WITHOUT calling .focus():
  // .focus() moves DOM focus back into the contenteditable, which steals
  // focus from the toolbar's hex input on every keystroke (TOPOL-tiptap bug).
  props.editor.chain().setColor(color).run();
}

function setPreviousColor() {
  if (editorColor.value) previousColor.value = editorColor.value;
}

watch(
  () => props.editor.state,
  () => {
    if (!props.editor.isFocused) return;
    if (
      localColor.value &&
      !props.editor.isActive("textStyle", {
        color: localColor.value,
      })
    ) {
      localColor.value = null;
    }
  },
);
</script>
